import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

/**
 * One session cookie per game slug (`gameToken_<slug>`), so a game launched in
 * one tab never replaces the session another tab is using.
 */
const TOKEN_COOKIE_PREFIX = 'gameToken_';
/** Pre-per-slug cookies; still read so existing sessions survive a deploy. */
const LEGACY_TOKEN_COOKIE = 'gameToken';
const LEGACY_GAME_SLUG_COOKIE = 'gameSlug';

const COOKIE_OPTIONS: Cookies.CookieAttributes = {
  expires: 1, // 1 day
  secure: true,
  sameSite: 'strict',
};

const tokenCookieName = (gameSlug: string) => `${TOKEN_COOKIE_PREFIX}${gameSlug}`;

/** The stored session token for one game, or null. */
export const readSessionToken = (gameSlug: string): string | null => {
  if (typeof window === 'undefined' || !gameSlug) return null;
  const token = Cookies.get(tokenCookieName(gameSlug));
  if (token) return token;
  return Cookies.get(LEGACY_GAME_SLUG_COOKIE) === gameSlug
    ? Cookies.get(LEGACY_TOKEN_COOKIE) || null
    : null;
};

interface AuthState {
  /** Session token of the game open in this tab. */
  accessGameToken: string | null;
  /** Slug of the game the token was issued for; a session token is only valid for one game. */
  gameSlug: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Filled per game by useGameSession, from a launch or from that game's cookie.
const initialState: AuthState = {
  accessGameToken: null,
  gameSlug: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Make a stored session (read with readSessionToken) the active one, without rewriting its cookie
    restoreSession: (state, action: PayloadAction<{ token: string; gameSlug: string }>) => {
      state.accessGameToken = action.payload.token;
      state.gameSlug = action.payload.gameSlug;
      state.isAuthenticated = true;
      state.error = null;
      state.isLoading = false;
    },

    // Set the session token once a launch token has been resolved
    setToken: (state, action: PayloadAction<{ token: string; gameSlug: string }>) => {
      const { token, gameSlug } = action.payload;
      state.accessGameToken = token;
      state.gameSlug = gameSlug;
      state.isAuthenticated = true;
      state.error = null;
      state.isLoading = false;

      Cookies.set(tokenCookieName(gameSlug), token, COOKIE_OPTIONS);
    },

    // Remove token on logout
    logout: (state) => {
      // Remove this game's cookie only; other tabs' sessions stay valid.
      if (state.gameSlug) {
        Cookies.remove(tokenCookieName(state.gameSlug));
        if (Cookies.get(LEGACY_GAME_SLUG_COOKIE) === state.gameSlug) {
          Cookies.remove(LEGACY_TOKEN_COOKIE);
          Cookies.remove(LEGACY_GAME_SLUG_COOKIE);
        }
      }

      state.accessGameToken = null;
      state.gameSlug = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
    },
  },
});

export const { setLoading, setError, setToken, restoreSession, logout } = authSlice.actions;
export default authSlice.reducer;
